import React, { useEffect, useState, useRef, useMemo } from "react";
import Draggable from "react-draggable";
import ConfirmationModal from "../../common/components/ConfirmationModal";
import Portal from "../../common/components/Portal";
import { useDispatch, useSelector } from "react-redux";
import action from "../actions";
import ReactPaginate from "react-paginate";
import LoadingOverlay from "react-loading-overlay";
import Loader from "../../common/components/Loader";
import ViewReportInfo from "../../smartCharts/components/ViewReportInfo";
import QueryEditModal from "../../smartCharts/components/SmartChartDataEditForm";
import GlobalSearch from "../../common/components/GlobalSearch";
import AdvancedFilters from "../../smartCharts/components/DocumentFilters";
import QueryViewModal from "./QueryViewModal";
import ReactTooltip from "react-tooltip";
import AssignQueryFilterToUsersModal from "./AssignQueryFilterToUsersModal";
import QueryCreatedUserFilter from "./QueryCreatedUserFilter";
import {
    clearQueryCreatedUserFilterPreference,
    getCachedProject,
    getQueryCreatedUserFilterPreference,
    getSessionDefaultProjectId,
    setQueryCreatedUserFilterPreference
} from "../../../utils/dashboardSession";
import { hasSavedFilterAdminAccess } from "../../users/constants";
import StarIcon from "../../../assets/img/star.svg";
import StarActiveIcon from "../../../assets/img/star-active.svg";
import { showAlert as showAlertUtil } from "../../../config/utils";
import { ERROR_COPY } from "../../../config/errorMessages";

const normalizeCreatedUserId = id => (id == null ? "" : String(id));

const getCreatedUserOptionIds = options =>
    (options || []).map(item => normalizeCreatedUserId(item.id ?? item.user_id)).filter(Boolean);

const getDefaultCreatedUserIds = (options, userId) => {
    const currentId = normalizeCreatedUserId(userId);
    const optionIds = getCreatedUserOptionIds(options);
    return currentId && optionIds.includes(currentId) ? [currentId] : [];
};

const buildQueryListParams = (params, createdUserIds, hasFilterAdminAccess) => {
    const apiParams = { ...params };
    if (params.favourite === "") {
        apiParams.favourite = "all";
        if (hasFilterAdminAccess && createdUserIds?.length) {
            apiParams.created_user_ids = createdUserIds.map(id => String(id));
        } else {
            delete apiParams.created_user_ids;
        }
    } else {
        delete apiParams.created_user_ids;
    }
    return apiParams;
};

export const QueriesModal = ({ queries = [], isSavedViews = false, queryType: queryTypeProp, ...props }) => {
    const [showConfirmation, setConfirmation] = useState(false);
    const { getProjectByIdResponse } = useSelector(state => state.projectReducer);
    const defaultProject = useSelector(state => state.dashboardReducer?.defaultProject);
    const sessionProjectId = getSessionDefaultProjectId(defaultProject);
    const effectiveProjectId = getProjectByIdResponse?.id || sessionProjectId;
    const cachedProject = getCachedProject(effectiveProjectId);
    const effectiveClientId = getProjectByIdResponse?.client?.id || cachedProject?.client?.id;
    const { getQueryData, getDashboard, deleteQueryResponse, updateQueryResponse, addFavouriteQueryData, assignFavouriteQueryData, getQueryFilterCreatedUsersData } = useSelector(
        state => state.dashboardReducer
    );
    const currentUserId = localStorage.getItem("userId");
    const queryType = queryTypeProp || (isSavedViews ? "table_view" : "filter");
    const hasFilterAdminAccess = hasSavedFilterAdminAccess(queryType);
    const storedCreatedUserIdsAtInit =
        hasFilterAdminAccess && effectiveProjectId ? getQueryCreatedUserFilterPreference(effectiveProjectId, isSavedViews) : null;
    const hasStoredCreatedUserFilter = storedCreatedUserIdsAtInit !== null;

    const [createdUserIds, setCreatedUserIds] = useState(() => storedCreatedUserIdsAtInit || []);
    const restoredCreatedUserProjectRef = useRef(hasStoredCreatedUserFilter ? effectiveProjectId : null);
    const pendingCreatedUserBootstrapRef = useRef(false);
    const paramsRef = useRef(null);
    const [isCreatedUserSessionReady, setIsCreatedUserSessionReady] = useState(() => !hasFilterAdminAccess || !!effectiveProjectId);
    const [queryData, setQueries] = useState([]);
    const [params, setParams] = useState({
        limit: 100,
        offset: 0,
        client_id: effectiveClientId,
        project_id: effectiveProjectId,
        favourite_user: localStorage?.getItem("userId"),
        settings_user_id: localStorage?.getItem("userId"),
        sort_by: "created_date",
        order: "desc",
        query_type: isSavedViews ? "table_view" : null,
        favourite: hasStoredCreatedUserFilter ? "" : "yes"
    });
    paramsRef.current = params;
    const [paginationParams, setPaginationParams] = useState({
        totalPages: 0,
        perPage: 100,
        currentPage: 0,
        totalCount: 0
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isQuerySet, setIsQuerySet] = useState(false);
    const [deleteQueryId, setDeleteQueryId] = useState(false);
    const [selectedQuery, setselectedQuery] = useState(null);
    const [QueryNameEdit, setQueryNameEdit] = useState(false);
    const [queryView, setQueryView] = useState(null);
    const [addToFav, setAddToFav] = useState(null);
    const [showAssignUsersToFavoritesModal, setShowAssignUsersToFavoritesModal] = useState(false);
    const [sortParams, setSortParams] = useState({ sort_by: "created_date", order: "desc" });
    const hasTriedFavoriteFallback = useRef(hasStoredCreatedUserFilter);
    const dispatch = useDispatch();
    const queryListParams = useMemo(
        () => buildQueryListParams(params, createdUserIds, hasFilterAdminAccess),
        [params, createdUserIds, hasFilterAdminAccess]
    );

    const createdUserNameById = useMemo(() => {
        const map = {};
        (getQueryFilterCreatedUsersData?.data || []).forEach(user => {
            const id = normalizeCreatedUserId(user.id ?? user.user_id);
            if (id && user.name) {
                map[id] = user.name;
            }
        });
        return map;
    }, [getQueryFilterCreatedUsersData]);

    useEffect(() => {
        const projectId = getProjectByIdResponse?.id || sessionProjectId;
        const clientId = getProjectByIdResponse?.client?.id || getCachedProject(projectId)?.client?.id;
        if (!projectId) {
            return;
        }
        setParams(prev => {
            if (prev.project_id === projectId && prev.client_id === clientId) {
                return prev;
            }
            return { ...prev, client_id: clientId, project_id: projectId };
        });
    }, [getProjectByIdResponse, sessionProjectId]);

    useEffect(() => {
        if (!hasFilterAdminAccess) {
            setIsCreatedUserSessionReady(true);
            return;
        }
        if (!params.project_id) {
            return;
        }
        if (restoredCreatedUserProjectRef.current === params.project_id) {
            setIsCreatedUserSessionReady(true);
            return;
        }
        const previousProjectId = restoredCreatedUserProjectRef.current;
        restoredCreatedUserProjectRef.current = params.project_id;

        if (previousProjectId && String(previousProjectId) !== String(params.project_id)) {
            clearQueryCreatedUserFilterPreference();
            pendingCreatedUserBootstrapRef.current = false;
            hasTriedFavoriteFallback.current = false;
            setCreatedUserIds([]);
            setParams(prev => ({ ...prev, favourite: "yes", offset: 0 }));
            setIsCreatedUserSessionReady(true);
            return;
        }

        const stored = getQueryCreatedUserFilterPreference(params.project_id, isSavedViews);
        if (stored !== null) {
            hasTriedFavoriteFallback.current = true;
            setCreatedUserIds(stored);
            setParams(prev => (prev.favourite === "" ? prev : { ...prev, favourite: "", offset: 0 }));
        }
        setIsCreatedUserSessionReady(true);
    }, [hasFilterAdminAccess, params.project_id, isSavedViews]);

    useEffect(() => {
        if (!params.project_id || !isCreatedUserSessionReady || pendingCreatedUserBootstrapRef.current) {
            return;
        }
        setIsLoading(true);
        dispatch(action.getQuery(queryListParams));
    }, [params.project_id, queryListParams, dispatch, isCreatedUserSessionReady]);

    useEffect(() => {
        return () => {
            dispatch(action.clearQueryFilterCreatedUsers());
        };
    }, [dispatch]);

    const fetchCreatedUsers = () => {
        if (!hasFilterAdminAccess || !params.project_id) {
            return;
        }
        dispatch(
            action.getQueryFilterCreatedUsers({
                client_id: params.client_id,
                project_id: params.project_id
            })
        );
    };

    useEffect(() => {
        if (!hasFilterAdminAccess || !params.project_id || !params.client_id) {
            return;
        }
        if (getQueryFilterCreatedUsersData?.data?.length || getQueryFilterCreatedUsersData?.isLoading) {
            return;
        }
        fetchCreatedUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasFilterAdminAccess, params.project_id, params.client_id]);

    useEffect(() => {
        if (!pendingCreatedUserBootstrapRef.current || !hasFilterAdminAccess) {
            return;
        }
        if (getQueryFilterCreatedUsersData?.isLoading) {
            return;
        }
        if (getQueryFilterCreatedUsersData?.success !== true && getQueryFilterCreatedUsersData?.success !== false) {
            return;
        }

        pendingCreatedUserBootstrapRef.current = false;
        const defaults = getDefaultCreatedUserIds(getQueryFilterCreatedUsersData?.data || [], currentUserId);
        setCreatedUserIds(defaults);
        setParams(prev => ({ ...prev, favourite: "", offset: 0 }));
    }, [getQueryFilterCreatedUsersData, hasFilterAdminAccess, currentUserId]);

    useEffect(() => {
        if (getQueryData?.isQueryLoading) {
            setIsLoading(true);
            return;
        }

        if (getQueryData?.success) {
            const transformedData = Object.values(getQueryData.data || {}).map(value => {
                const { query, ...rest } = value;
                return { ...rest, filter: query };
            });
            const currentFavourite = paramsRef.current?.favourite;

            if (
                currentFavourite === "yes" &&
                !hasTriedFavoriteFallback.current &&
                (!getQueryData?.count || transformedData.length === 0)
            ) {
                hasTriedFavoriteFallback.current = true;
                if (hasFilterAdminAccess && paramsRef.current?.project_id) {
                    pendingCreatedUserBootstrapRef.current = true;
                    setIsLoading(true);
                    dispatch(
                        action.getQueryFilterCreatedUsers({
                            client_id: paramsRef.current.client_id,
                            project_id: paramsRef.current.project_id
                        })
                    );
                    return;
                }
                setParams(prev => ({ ...prev, favourite: "", offset: 0 }));
                return;
            }

            setQueries(transformedData);
            setIsLoading(false);
            setPaginationParams(prev => ({
                ...prev,
                totalCount: getQueryData?.count || 0,
                totalPages: Math.ceil((getQueryData?.count || 0) / prev.perPage)
            }));
            return;
        }

        if (getQueryData && getQueryData.success === false) {
            setIsLoading(false);
        }
    }, [getQueryData, hasFilterAdminAccess, dispatch]);
    useEffect(() => {
        if (updateQueryResponse?.success && QueryNameEdit) {
            showLongAlert(`${view} updated successfully`);
        }
        setQueryNameEdit(false);
    }, [updateQueryResponse]);
    useEffect(() => {
        if (deleteQueryResponse && isQuerySet) {
            showLongAlert(`${view} deleted successfully`);
            setIsQuerySet(false);
        }
    }, [deleteQueryResponse]);

    useEffect(() => {
        ReactTooltip.rebuild();
    }, [queryData]);

    useEffect(() => {
        if (getDashboard) {
            setIsQuerySet(false);
        }
    }, [getDashboard]);
    useEffect(() => {
        if (addToFav === "favourite") {
            if (addFavouriteQueryData?.success) {
                if (addFavouriteQueryData?.message === "marked as favourite") {
                    showLongAlert("Marked as favourite successfully");
                } else {
                    showLongAlert("Removed from favourite successfully");
                }
            } else {
                showLongAlert(ERROR_COPY.GENERIC);
            }
        }
        if (addToFav === "assignFavourite") {
            if (assignFavouriteQueryData?.success) {
                showLongAlert("Assigned users successfully");
            } else {
                showLongAlert(ERROR_COPY.GENERIC);
            }
        }
        setAddToFav(null);
    }, [addFavouriteQueryData, assignFavouriteQueryData]);
    const onApply = item => {
        setIsQuerySet(true);
        props.submitMasterFilter(item);
        props.onCancel();
    };

    const onReset = item => {
        setIsQuerySet(true);
        props.handleResetQuery(item);
        props.onCancel();
    };
    const showLongAlert = msg => showAlertUtil(msg, true);

    const renderConfirmationModal = () => {
        if (!showConfirmation) return null;
        return (
            <Portal
                body={
                    <ConfirmationModal
                        heading={"Do you want to reset current dashboard filter and apply this query?"}
                        type="cancel"
                        message={""}
                        onNo={() => setConfirmation(false)}
                        onYes={() => {
                            props.onCancel();
                            props.submitMasterFilter();
                        }}
                    />
                }
                onCancel={() => setConfirmation(false)}
            />
        );
    };
    const isActive = item => {
        return queries?.length > 0 && queries?.find(query => query.id === item.id);
    };
    const handlePerPageChange = async e => {
        setPaginationParams({ ...paginationParams, perPage: e.target.value, currentPage: 0 });
        setParams({ ...params, offset: 0, limit: e.target.value });
    };

    const handlePageClick = async page => {
        setPaginationParams({ ...paginationParams, currentPage: page.selected });
        setParams({
            ...params,
            offset: page.selected
        });
    };
    const renderDeleteConfirmationModal = () => {
        if (!deleteQueryId) return null;
        return (
            <Portal
                body={
                    <ConfirmationModal
                        heading={`Do you want to delete this query?`}
                        message={"This action cannot be reverted, are you sure that you need to delete this item?"}
                        onNo={() => setDeleteQueryId(null)}
                        onYes={() => {
                            dispatch(action?.deleteQuery(deleteQueryId, queryListParams));
                            setDeleteQueryId(null);
                            setIsQuerySet(true);
                        }}
                    />
                }
                onCancel={() => setDeleteQueryId(null)}
            />
        );
    };
    const renderReportInforModal = () => {
        if (!selectedQuery) return null;

        return (
            <Portal
                body={<ViewReportInfo reportData={selectedQuery} onCancel={() => setselectedQuery(null)} isQuerModal={true} />}
                onCancel={() => setselectedQuery(null)}
            />
        );
    };
    const renderQueryViewModal = () => {
        if (!queryView) return null;

        return (
            <Portal
                body={<QueryViewModal viewData={queryView?.query_label} onCancel={() => setQueryView(null)} />}
                onCancel={() => setQueryView(null)}
            />
        );
    };
    const updateQuery = (data, selectedQuery) => {
        dispatch(
            action?.updateFilterQuery(
                {
                    name: data?.name,
                    modified_by: localStorage?.getItem("userId"),
                    notes: data?.notes,
                    remember_on_reset: data?.remember_on_reset,
                    remember_on_logout: data?.remember_on_logout,
                    is_filter_applied: data?.remember_on_reset || data?.remember_on_logout
                },
                selectedQuery?.id,
                queryListParams
            )
        );
        setQueryNameEdit("update Submitted");
    };
    const renderImageBandEditModal = () => {
        if (!QueryNameEdit || QueryNameEdit === "update Submitted") return null;
        return (
            <Portal
                body={
                    <QueryEditModal
                        onCancel={() => setQueryNameEdit(false)}
                        heading={"Saved Filter Settings"}
                        updateData={data => updateQuery(data, QueryNameEdit)}
                        buttonText={"Update"}
                        hasNotes={true}
                        selectedData={QueryNameEdit}
                        isQuery={true}
                        hasAdvancedRememberSettings={!isSavedViews}
                    />
                }
                onCancel={() => setQueryNameEdit(false)}
            />
        );
    };
    const handleSortData = (key, value) => {
        setParams({
            ...params,
            [key]: value
        });
        setSortParams({ ...sortParams, [key]: value });
    };
    const handleSelectViewType = value => {
        setParams({
            ...params,
            favourite: value,
            offset: 0
        });
        setPaginationParams({
            ...paginationParams,
            currentPage: 0
        });
    };
    const handleGlobalSearch = async search => {
        setParams({
            ...params,
            offset: 0,
            search
        });
        setPaginationParams({
            ...paginationParams,
            currentPage: 0
        });
    };
    const resetAllFilters = item => {
        props.resetAllQueryFilters(item);
        props.onCancel();
    };

    const handleCreatedUserFilterChange = ids => {
        const nextIds = ids.map(id => String(id));
        setCreatedUserIds(nextIds);
        setQueryCreatedUserFilterPreference(params.project_id, isSavedViews, nextIds);
        setParams(prev => ({ ...prev, offset: 0 }));
        setPaginationParams(prev => ({ ...prev, currentPage: 0 }));
    };

    const addToFavorite = async id => {
        let currentUser = localStorage.getItem("userId") || "";
        dispatch(action.addQueriesToFavorites({ user_id: currentUser, query_filter_id: id }, queryListParams));
        setAddToFav("favourite");
    };
    const toggleAssignUsersToFavoriteModal = (item = "") => {
        setShowAssignUsersToFavoritesModal(item);
    };
    const renderAssignUsersToFavoriteModal = () => {
        if (!showAssignUsersToFavoritesModal) return null;
        return (
            <Portal
                body={
                    <AssignQueryFilterToUsersModal
                        onCancel={() => toggleAssignUsersToFavoriteModal(null)}
                        clientId={getProjectByIdResponse?.client?.id}
                        onAssignUsers={onAssignFavoriteToUsers}
                        selectedQueryId={showAssignUsersToFavoritesModal || null}
                        isSavedViews={isSavedViews}
                    />
                }
                onCancel={() => toggleAssignUsersToFavoriteModal(null)}
            />
        );
    };
    const onAssignFavoriteToUsers = async value => {
        toggleAssignUsersToFavoriteModal();
        setAddToFav("assignFavourite");
        dispatch(action?.assignFavoriteQueryToUsers(value, queryListParams));
    };

    const view = isSavedViews ? "View" : "Filter";
    return (
        <>
            <div
                className="modal assign-init-modal image-pull-modal"
                style={{ display: "block" }}
                id="modalId"
                tabIndex="-1"
                role="dialog"
                aria-labelledby="exampleModalLabel"
                // aria-hidden="true"
            >
                {renderConfirmationModal()}
                <Draggable handle=".draggable-fav-modal" cancel=".close, .not-draggable">
                    <div className="modal-dialog assignModal" role="document">
                        <div className="modal-content">
                            <div className="modal-header draggable-fav-modal draggable pad-heading">
                                <div className="d-flex align-items-center">
                                    <ReactTooltip id="saved-filter-bookmark" place="bottom" effect="solid" backgroundColor="#007bff" />
                                    <h5 className="modal-title d-inline-flex align-items-center mt-0" id="exampleModalLabel">
                                        <span>{isSavedViews ? "Saved Views" : "Saved Filters"}</span>
                                        {!isSavedViews && props.toggleSavedFiltersBookmark && (
                                            <button
                                                type="button"
                                                className="not-draggable"
                                                data-tip={
                                                    props.isSavedFiltersBookmarked
                                                        ? "Remove this from Dashboard Bookmarks"
                                                        : "Add this to Dashboard Bookmarks"
                                                }
                                                data-for="saved-filter-bookmark"
                                                onClick={e => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    props.toggleSavedFiltersBookmark();
                                                }}
                                                style={{
                                                    alignItems: "center",
                                                    background: "transparent",
                                                    border: 0,
                                                    display: "inline-flex",
                                                    height: "14px",
                                                    justifyContent: "center",
                                                    lineHeight: 1,
                                                    marginLeft: "6px",
                                                    padding: 0,
                                                    position: "relative",
                                                    top: "1px",
                                                    width: "14px",
                                                    marginBottom: "4px"
                                                }}
                                            >
                                                <img
                                                    src={props.isSavedFiltersBookmarked ? StarActiveIcon : StarIcon}
                                                    alt={props.isSavedFiltersBookmarked ? "Remove from Bookmarks" : "Add to Bookmarks"}
                                                    style={{ display: "block", width: "11px", height: "11px" }}
                                                />
                                            </button>
                                        )}
                                    </h5>
                                </div>
                                <button type="button" className="close" data-dismiss="modal" aria-label="Close" onClick={() => props.onCancel()}>
                                    <span aria-hidden="true">
                                        <img src="/img/close.svg" alt="" />
                                    </span>
                                </button>
                            </div>
                            <LoadingOverlay active={isLoading || isQuerySet} spinner={<Loader />} fadeSpeed={10}>
                                <form autoComplete="nope" onSubmit={e => e.preventDefault()}>
                                    <div className="modal-body ">
                                        <div className="form-group mb-2">
                                            <div className="formInp">
                                                <div className="dashboard-outer">
                                                    <div className="outer-detail">
                                                        <div className="right-panel-section">
                                                            <div className="dtl-sec system-building col-md-12 ">
                                                                <div className="tab-dtl region-mng">
                                                                    <div className="tab-active recomdn-table bg-grey-table pd-table-btm ">
                                                                        <div className="table-top-menu allign-right align-items-center">
                                                                            <div className="rgt query-filter">
                                                                                <AdvancedFilters
                                                                                    handleSortData={handleSortData}
                                                                                    handleSelectViewType={handleSelectViewType}
                                                                                    sortParams={sortParams}
                                                                                    entityKey={"queries"}
                                                                                    viewType={params.favourite}
                                                                                    leadingFilter={
                                                                                        hasFilterAdminAccess ? (
                                                                                            <QueryCreatedUserFilter
                                                                                                selectedIds={createdUserIds}
                                                                                                onChange={handleCreatedUserFilterChange}
                                                                                                onFetchOptions={fetchCreatedUsers}
                                                                                                options={getQueryFilterCreatedUsersData?.data || []}
                                                                                                isLoading={getQueryFilterCreatedUsersData?.isLoading}
                                                                                                disabled={params.favourite !== ""}
                                                                                            />
                                                                                        ) : null
                                                                                    }
                                                                                />
                                                                                <GlobalSearch
                                                                                    handleGlobalSearch={handleGlobalSearch}
                                                                                    globalSearchKey={params?.search}
                                                                                    customClassName={"search-sm-chrt"}
                                                                                />
                                                                                <div className="search">
                                                                                    <button
                                                                                        className="btn btn-grid filtr-grid search-sm-chrt search-icn m-5"
                                                                                        data-tip="Reset all saved filters"
                                                                                        data-for="reset"
                                                                                        onClick={e => {
                                                                                            e.preventDefault();
                                                                                            resetAllFilters();
                                                                                        }}
                                                                                    >
                                                                                        <img src="/img/refresh-dsh.svg" alt="" className="fil-ico" />
                                                                                    </button>
                                                                                    <ReactTooltip
                                                                                        id="reset"
                                                                                        effect="solid"
                                                                                        place="left"
                                                                                        backgroundColor="#007bff"
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <div
                                                                            className={`dtl-sec col-md-12 height-full padd-report view-pad height-query-overflow`}
                                                                        >
                                                                            <div className="tab-dtl region-mng smchart-exp top-smrt-exp query-filbox mt-0">
                                                                                <div className={`tab-dtl region-mng rpt-height`}>
                                                                                    <div
                                                                                        className={`tab-active location-sec chart-smart query-popup`}
                                                                                    >
                                                                                        <ReactTooltip
                                                                                            id="Query"
                                                                                            effect="solid"
                                                                                            place="bottom"
                                                                                            backgroundColor="#007bff"
                                                                                        />
                                                                                        {!isLoading && queryData?.length > 0 ? (
                                                                                            <div className={`queries-outer-height dtl-sec`}>
                                                                                                <div className="row m-0 container-flexlay">
                                                                                                    <div className="smart-lst-outr col-md-12 d-flex">
                                                                                                        {queryData.map(item => {
                                                                                                            const createdByUserId = normalizeCreatedUserId(item.created_by_user_id);
                                                                                                            const isOtherUserFilter =
                                                                                                                !!createdByUserId &&
                                                                                                                createdByUserId !== normalizeCreatedUserId(currentUserId);
                                                                                                            const canManageFilter = !isOtherUserFilter || hasFilterAdminAccess;
                                                                                                            const createdByName = isOtherUserFilter
                                                                                                                ? item.created_by_name ||
                                                                                                                  item.created_by ||
                                                                                                                  item.created_user ||
                                                                                                                  createdUserNameById[createdByUserId] ||
                                                                                                                  ""
                                                                                                                : "";
                                                                                                            return (
                                                                                                            <div
                                                                                                                key={item?.id}
                                                                                                                className={`itms col-md-4 mr-3 ${
                                                                                                                    isActive(item)
                                                                                                                        ? "bg-reset-active"
                                                                                                                        : item.remember_on_reset ||
                                                                                                                          item.remember_on_logout
                                                                                                                        ? "remember-active"
                                                                                                                        : isOtherUserFilter
                                                                                                                        ? "other-user-filter"
                                                                                                                        : ""
                                                                                                                }`}
                                                                                                            >
                                                                                                                <div className="btn-view btn-smart-rgt d-flex">
                                                                                                                    <div
                                                                                                                        className="info-more-btn cursor-pointer"
                                                                                                                        data-tip="More Information"
                                                                                                                        data-for="Query"
                                                                                                                        onClick={e => {
                                                                                                                            e.preventDefault();
                                                                                                                            setselectedQuery(item);
                                                                                                                        }}
                                                                                                                    >
                                                                                                                        <i className="fa fa-info"></i>{" "}
                                                                                                                        More Info
                                                                                                                    </div>

                                                                                                                    <div className="sm-btn-wrapper d-flex btn-edits">
                                                                                                                        <button
                                                                                                                            className="btn btn-edit btn-fav btn-single-fav"
                                                                                                                            data-tip={
                                                                                                                                item.favourite ===
                                                                                                                                "True"
                                                                                                                                    ? "Remove From Favorite"
                                                                                                                                    : "Add To Favorite"
                                                                                                                            }
                                                                                                                            data-for="Query"
                                                                                                                            onClick={e => {
                                                                                                                                e.preventDefault();
                                                                                                                                addToFavorite(
                                                                                                                                    item?.id
                                                                                                                                );
                                                                                                                            }}
                                                                                                                        >
                                                                                                                            <img
                                                                                                                                src={
                                                                                                                                    item.favourite ===
                                                                                                                                    "True"
                                                                                                                                        ? "/img/sm-charts-bold-heart.svg"
                                                                                                                                        : "/img/sm-charts-linear-heart.svg"
                                                                                                                                }
                                                                                                                                alt=""
                                                                                                                            />
                                                                                                                        </button>
                                                                                                                        {hasFilterAdminAccess ? (
                                                                                                                            <button
                                                                                                                                className="btn btn-edit btn-assign-user"
                                                                                                                                data-tip={
                                                                                                                                    isSavedViews
                                                                                                                                        ? "Assign View To Users"
                                                                                                                                        : "Assign Filter To Users"
                                                                                                                                }
                                                                                                                                data-for="Query"
                                                                                                                                onClick={e => {
                                                                                                                                    e.preventDefault();
                                                                                                                                    toggleAssignUsersToFavoriteModal(
                                                                                                                                        item.id
                                                                                                                                    );
                                                                                                                                }}
                                                                                                                            >
                                                                                                                                <img
                                                                                                                                    src="/img/person-icon.svg"
                                                                                                                                    alt=""
                                                                                                                                />
                                                                                                                            </button>
                                                                                                                        ) : null}
                                                                                                                        {canManageFilter ? (
                                                                                                                            <>
                                                                                                                                <div className="btn-hv-del btn-view">
                                                                                                                                    <button
                                                                                                                                        className={`btn btn-del sm-charts-report-delete ${
                                                                                                                                            queries?.find(
                                                                                                                                                e =>
                                                                                                                                                    e.id ===
                                                                                                                                                    item?.id
                                                                                                                                            )
                                                                                                                                                ? "cursor-disabled"
                                                                                                                                                : ""
                                                                                                                                        }`}
                                                                                                                                        data-tip={
                                                                                                                                            queries?.find(
                                                                                                                                                e =>
                                                                                                                                                    e.id ===
                                                                                                                                                    item?.id
                                                                                                                                            )
                                                                                                                                                ? `Applied ${view} cannot be deleted.`
                                                                                                                                                : `Delete ${view}`
                                                                                                                                        }
                                                                                                                                        data-for="Query"
                                                                                                                                        onClick={event => {
                                                                                                                                            event.preventDefault();
                                                                                                                                            setDeleteQueryId(
                                                                                                                                                item?.id
                                                                                                                                            );
                                                                                                                                        }}
                                                                                                                                        disabled={queries?.find(
                                                                                                                                            e =>
                                                                                                                                                e.id ===
                                                                                                                                                item?.id
                                                                                                                                        )}
                                                                                                                                    >
                                                                                                                                        <img
                                                                                                                                            src="/img/blue-delete.svg"
                                                                                                                                            alt=""
                                                                                                                                        />
                                                                                                                                    </button>
                                                                                                                                </div>
                                                                                                                                <div>
                                                                                                                                    <button
                                                                                                                                        className="btn btn-edit"
                                                                                                                                        data-tip={`Edit ${view}`}
                                                                                                                                        data-for="Query"
                                                                                                                                        onClick={e => {
                                                                                                                                            e.preventDefault();
                                                                                                                                            setQueryNameEdit(
                                                                                                                                                item
                                                                                                                                            );
                                                                                                                                        }}
                                                                                                                                    >
                                                                                                                                        <img
                                                                                                                                            src="/img/Icon-material-mode-edit.svg"
                                                                                                                                            alt=""
                                                                                                                                        />
                                                                                                                                    </button>
                                                                                                                                </div>
                                                                                                                            </>
                                                                                                                        ) : null}
                                                                                                                        <button
                                                                                                                            className="btn btn-edit"
                                                                                                                            onClick={e => {
                                                                                                                                e.preventDefault();
                                                                                                                                setQueryView(item);
                                                                                                                            }}
                                                                                                                            data-tip={`View details`}
                                                                                                                            data-for="Query"
                                                                                                                        >
                                                                                                                            <img
                                                                                                                                src="/img/view-eye-smart-chart.svg"
                                                                                                                                alt=""
                                                                                                                            />
                                                                                                                        </button>
                                                                                                                    </div>
                                                                                                                </div>
                                                                                                                <div className="heading-sec flex-column d-flex">
                                                                                                                    <div className="head-area">
                                                                                                                        <div className="label-section-prt align-items-center">
                                                                                                                            <div className="img-sec-prt">
                                                                                                                                <img
                                                                                                                                    src="/img/vuesax-bold-status-up.svg"
                                                                                                                                    alt=""
                                                                                                                                />
                                                                                                                            </div>
                                                                                                                            <div className="text-prt ">
                                                                                                                                <h3
                                                                                                                                    className="text-line-clamp-2 mb-0"
                                                                                                                                    data-for="error2"
                                                                                                                                >
                                                                                                                                    {item?.name}
                                                                                                                                </h3>
                                                                                                                            </div>
                                                                                                                        </div>
                                                                                                                        <div className="query-para-txt">
                                                                                                                            <p>{item.notes || "-"}</p>
                                                                                                                        </div>
                                                                                                                    </div>

                                                                                                                    <div className="btn-view btn-smart-rgt"></div>
                                                                                                                </div>
                                                                                                                {isOtherUserFilter && createdByName ? (
                                                                                                                    <div className="query-created-by-label" title={createdByName}>
                                                                                                                        Created by <span className="query-created-by-name">{createdByName}</span>
                                                                                                                    </div>
                                                                                                                ) : null}
                                                                                                                <div className="d-flex btn-core-outer">
                                                                                                                    <div className="botm-sec run-rpt-btn ">
                                                                                                                        {isActive(item) ? (
                                                                                                                            <button
                                                                                                                                className="btn btn-reset btn btn-reg col-md-9"
                                                                                                                                onClick={e => {
                                                                                                                                    e.preventDefault();
                                                                                                                                    onReset(item);
                                                                                                                                }}
                                                                                                                            >
                                                                                                                                Reset {view}
                                                                                                                                <span>
                                                                                                                                    <i className="fas fa-sync ml-2"></i>
                                                                                                                                </span>
                                                                                                                            </button>
                                                                                                                        ) : (
                                                                                                                            <button
                                                                                                                                className="btn btn btn-reg col-md-9"
                                                                                                                                onClick={e => {
                                                                                                                                    e.preventDefault();
                                                                                                                                    onApply(item);
                                                                                                                                }}
                                                                                                                            >
                                                                                                                                Apply {view}
                                                                                                                                <span>
                                                                                                                                    <i className="fa fa-arrow-right ml-2"></i>
                                                                                                                                </span>
                                                                                                                            </button>
                                                                                                                        )}
                                                                                                                        {!isSavedViews && canManageFilter && (
                                                                                                                            <button
                                                                                                                                className="btn btn-sm btn-light col-md-3 ml-2"
                                                                                                                                onClick={e => {
                                                                                                                                    e.preventDefault();
                                                                                                                                    setQueryNameEdit(
                                                                                                                                        item
                                                                                                                                    );
                                                                                                                                }}
                                                                                                                                aria-label="Advanced settings"
                                                                                                                                data-tip={`Filter Settings`}
                                                                                                                                data-for="Query"
                                                                                                                            >
                                                                                                                                <i className="fas fa-cog"></i>
                                                                                                                                {(item.remember_on_reset ||
                                                                                                                                    item.remember_on_logout) && (
                                                                                                                                    <span
                                                                                                                                        style={{
                                                                                                                                            position:
                                                                                                                                                "absolute",
                                                                                                                                            top: "4px",
                                                                                                                                            right: "4px",
                                                                                                                                            width: "8px",
                                                                                                                                            height: "8px",
                                                                                                                                            backgroundColor:
                                                                                                                                                "#007bff",
                                                                                                                                            borderRadius:
                                                                                                                                                "50%"
                                                                                                                                        }}
                                                                                                                                    />
                                                                                                                                )}
                                                                                                                            </button>
                                                                                                                        )}
                                                                                                                    </div>
                                                                                                                </div>
                                                                                                            </div>
                                                                                                            );
                                                                                                        })}
                                                                                                    </div>
                                                                                                </div>
                                                                                            </div>
                                                                                        ) : (
                                                                                            <div className="no-data-section">
                                                                                                <img src="/img/no-data.svg" alt="no-data-img" />
                                                                                                <h3>No Data Found</h3>
                                                                                                <p>There is no data to show you right now!!!</p>
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                                <div className="table-bottom d-flex mb-3">
                                    <div className="count d-flex col-md-6">
                                        <div className="count-dtl">
                                            Total Count: <span>{paginationParams.totalCount}</span>
                                        </div>
                                        <div className="col-md-2 pr-2 selbx">
                                            <select className="form-control" value={paginationParams.perPage} onChange={e => handlePerPageChange(e)}>
                                                <option value="10">10</option>
                                                <option value="20">20</option>
                                                <option value="30">30</option>
                                                <option value="40">40</option>
                                                <option value="50">50</option>
                                                <option value="100">100</option>
                                                <option value="150">150</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="pagination-sec col-md-6">
                                        <ReactPaginate
                                            previousLabel={
                                                <span data-place="top" data-effect="solid" data-tip={`Previous`} data-background-color="#007bff">
                                                    &lt;
                                                </span>
                                            }
                                            nextLabel={
                                                <span data-place="top" data-effect="solid" data-tip={`Next`} data-background-color="#007bff">
                                                    &gt;
                                                </span>
                                            }
                                            breakLabel={"..."}
                                            breakClassName={"break-me"}
                                            pageCount={paginationParams.totalPages}
                                            marginPagesDisplayed={2}
                                            pageRangeDisplayed={5}
                                            onPageChange={handlePageClick}
                                            containerClassName={"pagination"}
                                            subContainerClassName={"pages pagination"}
                                            activeClassName={"active"}
                                            activeLinkClassName={"active"}
                                            forcePage={paginationParams.currentPage}
                                        />
                                    </div>
                                </div>
                            </LoadingOverlay>
                        </div>
                    </div>
                </Draggable>
                {renderDeleteConfirmationModal()}
                {renderReportInforModal()}
                {renderImageBandEditModal()}
                {renderQueryViewModal()}
                {renderAssignUsersToFavoriteModal()}
            </div>
        </>
    );
};
